<script setup lang="ts">
  import { ref } from 'vue'
  import { onClickOutside, useClipboard } from '@vueuse/core'
  import { useAudiobooksMenuStore } from '@/stores/list-item-menu'
  import { usePopupStore } from '@/stores/popup'
  import { useAudiobooksStore } from '@/stores/audiobooks'
  import { useBrowserStore } from '@/stores/browser'
  import { useSnackbarStore } from '@/stores/snackbar'
  import { useLoaderStore } from '@/stores/loader'
  import { useModalStore } from '@/stores/modal'
  import { useQRCodeStore } from '@/stores/qr-code'
  import { useFileInfoStore } from '@/stores/file-info'
  import type { TAudiobookPreview, TProjectAudiobook } from '@/types/audiobooks'
  import type { TDraft } from '@/types/drafts'
  import DesMenuOptions from '@/components/menu/DesMenuOptions.vue'
  import DesMenuItem from '@/components/menu/DesMenuItem.vue'
  import DesMenuSeparator from '@/components/menu/DesMenuSeparator.vue'

  const menu = useAudiobooksMenuStore()
  const popup = usePopupStore()
  const audiobooks = useAudiobooksStore()
  const snack = useSnackbarStore()
  const loader = useLoaderStore()
  const modal = useModalStore()
  const qr = useQRCodeStore()
  const browser = useBrowserStore()
  const fileInfo = useFileInfoStore()

  const target = ref<HTMLElement | null>(null)
  onClickOutside(target, () => menu.item && menu.close())

  const preview = async () => {
    if (!menu.item) return

    const url = await audiobooks.getAudiobookPreviewUrl(menu.item)
    await browser.openNewWindowTab(url)
  }

  const copyUrl = async () => {
    if (menu.item?.url) {
      try {
        await useClipboard().copy(menu.item?.url)
        snack.add('URL copied to clipboard')
      } catch {
        snack.add('Unable to copy URL to clipboard')
      }
    }
  }

  async function deleteProject() {
    if (!menu.item) return

    const result = await popup.confirm('Are you sure you want to delete this audiobook?', {
      content: 'This action is irreversible.',
      labelCancel: 'Cancel',
      labelConfirm: 'Delete',
    })

    if (result !== false) {
      await audiobooks.deleteAudiobook(menu.item)
    }
  }

  async function generateQRcode() {
    if (!menu?.item?.id || isNaN(menu.item.id)) {
      return
    }

    loader.show('Getting data...')
    if (!menu.item.token) {
      menu.item = await addToken(menu.item)
    }

    const formats = await qr.attemptToGetAudioFormats(menu.item)
    loader.hide()

    if (formats.length) {
      modal.open(() => import('@/components/modal/DesModalQR.vue'), {
        formats,
      })
    }
  }

  async function generateAudiobookEmbedCode() {
    if (!menu?.item?.id || isNaN(menu.item.id)) {
      return
    }

    loader.show('Getting data...')
    let project = menu.item
    if (!project.token) {
      project = await addToken(project)
    }
    loader.hide()

    modal.open(() => import('@/components/modal/DesModalAudiobookEmbed.vue'), {
      project,
    })
  }

  async function retryChapterUpload() {
    if (!menu?.item?.id || isNaN(menu.item.id)) {
      return
    }

    loader.show('Uploading chapter files...')
    const res = await audiobooks.retryChapterUpload(menu.item.id)
    loader.hide()

    if (res.result) {
      menu.item.chapters_state = 'complete'
      snack.add('Every chapter file is published now', { type: 'success' })

      return
    }
    snack.add(res.message ?? 'The upload could not be retried', { type: 'danger' })
  }

  function exportForAcx() {
    if (!menu?.item?.id || isNaN(menu.item.id)) {
      return
    }

    modal.open(() => import('@/components/modal/DesModalAudiobookAcxExport.vue'), {
      audiobookId: menu.item.id,
      title: menu.item.name,
    })
  }

  function exportForM4b() {
    if (!menu?.item?.id || isNaN(menu.item.id)) {
      return
    }

    modal.open(() => import('@/components/modal/DesModalAudiobookM4bExport.vue'), {
      audiobookId: menu.item.id,
      title: menu.item.name,
    })
  }

  async function addToken(project: TProjectAudiobook): Promise<TProjectAudiobook> {
    const previewData: TAudiobookPreview | null = await audiobooks.preview(project.id)
    const resToken = previewData?.token ? previewData.token : null

    project.token = project.token ? project.token : resToken

    return project
  }

  async function fileInformation() {
    if (!menu?.item) {
      return
    }

    const draft: TDraft | null = await fileInfo.getProjectDraft(menu.item)

    modal.open(() => import('@/components/modal/DesModalFileInfo.vue'), {
      item: menu.item,
      draft: draft,
    })
  }
</script>

<template>
  <div
    class="absolute z-50"
    :style="{ left: menu.position.left + 'px', top: menu.position.top + 'px' }"
    ref="target"
    @click="menu.close()"
  >
    <DesMenuOptions :open="menu.opened" :left="menu.flip" :right="!menu.flip">
      <template v-if="menu.item?.status === 2">
        <DesMenuItem @click="copyUrl()" icon="fa-light fa-link" event="projects-audiobook-menu">
          Copy URL to mp3
        </DesMenuItem>
        <DesMenuItem @click="generateQRcode()" icon="fa-light fa-qrcode" event="projects-audiobook-menu">
          Generate QR code
        </DesMenuItem>
        <DesMenuItem @click="generateAudiobookEmbedCode()" icon="fa-light fa-file-code" event="projects-audiobook-menu">
          Generate audiobook embed code
        </DesMenuItem>
        <DesMenuItem @click="preview()" icon="fa-light fa-eye" event="projects-audiobook-menu">
          Open audiobook preview
        </DesMenuItem>
        <DesMenuItem @click="exportForAcx()" icon="fa-light fa-file-zipper" event="projects-audiobook-menu">
          Export for ACX (Audible)
        </DesMenuItem>
        <DesMenuItem @click="exportForM4b()" icon="fa-light fa-file-audio" event="projects-audiobook-menu">
          Export as m4b audiobook
        </DesMenuItem>
        <DesMenuItem
          v-if="menu.item?.chapters_state === 'partial'"
          @click="retryChapterUpload()"
          icon="fa-light fa-rotate-right"
          event="projects-audiobook-menu"
        >
          Retry chapter upload
        </DesMenuItem>
        <DesMenuSeparator />
      </template>
      <DesMenuItem @click="fileInformation()" icon="fa-light fa-circle-info" event="projects-audiobook-menu">
        Project information
      </DesMenuItem>
      <DesMenuItem @click="deleteProject()" icon="fa-light fa-trash-can" danger event="projects-audiobook-menu">
        Delete
      </DesMenuItem>
    </DesMenuOptions>
  </div>
</template>
